import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { kpiAPI, reportsAPI } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import AccessibleModal from '../components/common/AccessibleModal';

const toDateTimeLocal = (date) => format(date, "yyyy-MM-dd'T'HH:mm");
const isBadgeScanEntry = (entry) => entry?.source === 'card_reader' || !!entry?.badge_uid;

const ReportsPage = () => {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const tr = (fr, en) => (isFr ? fr : en);
  const { user } = useAuth();
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  const isUsager = user?.role === 'usager';
  const [timesheets, setTimesheets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [operations, setOperations] = useState(null);
  const [safety, setSafety] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [patientKpis, setPatientKpis] = useState([]);
  const [surveyType, setSurveyType] = useState('meal');
  const [surveyRating, setSurveyRating] = useState('5');
  const [surveyComment, setSurveyComment] = useState('');
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [surveyMessage, setSurveyMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [startDateTime, setStartDateTime] = useState(toDateTimeLocal(startOfMonth(new Date())));
  const [endDateTime, setEndDateTime] = useState(toDateTimeLocal(endOfMonth(new Date())));
  const [showPdfOptions, setShowPdfOptions] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showCsvOptions, setShowCsvOptions] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [pdfStartDateTime, setPdfStartDateTime] = useState(toDateTimeLocal(startOfMonth(new Date())));
  const [pdfEndDateTime, setPdfEndDateTime] = useState(toDateTimeLocal(endOfMonth(new Date())));
  const [pdfMode, setPdfMode] = useState('all');
  const [csvStartDateTime, setCsvStartDateTime] = useState(toDateTimeLocal(startOfMonth(new Date())));
  const [csvEndDateTime, setCsvEndDateTime] = useState(toDateTimeLocal(endOfMonth(new Date())));
  const [csvMode, setCsvMode] = useState('all');
  const [pdfBlocks, setPdfBlocks] = useState({
    overviewStats: true,
    globalKpis: true,
    patientKpisOverview: true,
    topPatientKpis: true,
    workHoursTop10: true,
    timeCenterTop10: true,
    timeCenterStats: true,
    lateness: true,
    operations: true,
    safety: true,
    estimatedCosts: true,
    staffLoad: true,
    patientKpiTable: true,
    incidentsToProcess: true,
    timesheets: true,
  });
  const [csvBlocks, setCsvBlocks] = useState({
    overviewStats: true,
    globalKpis: true,
    patientKpisOverview: true,
    topPatientKpis: true,
    workHoursTop10: true,
    timeCenterTop10: true,
    timeCenterStats: true,
    lateness: true,
    operations: true,
    safety: true,
    estimatedCosts: true,
    staffLoad: true,
    patientKpiTable: true,
    incidentsToProcess: true,
    timesheets: true,
  });
  const [tsEmployeeFilter, setTsEmployeeFilter] = useState('all');
  const [tsNoFilter, setTsNoFilter] = useState('all');
  const [tsDateFilter, setTsDateFilter] = useState('all');
  const [tsEntryFilter, setTsEntryFilter] = useState('all');
  const [tsExitFilter, setTsExitFilter] = useState('all');
  const [tsHoursFilter, setTsHoursFilter] = useState('all');
  const [tsMinutesFilter, setTsMinutesFilter] = useState('all');
  const [tsCenterFilter, setTsCenterFilter] = useState('all');
  const isManager = user?.role === 'manager';
  const reportParams = isManager
    ? { start: startDateTime, end: endDateTime }
    : { start: startDateTime, end: endDateTime, center_id: undefined };

  const pdfBlockList = [
    { key: 'overviewStats', label: tr('Résumé statistique', 'Summary statistics') },
    { key: 'globalKpis', label: tr('KPI globaux', 'Global KPIs') },
    { key: 'patientKpisOverview', label: tr('KPI usagers', 'Patient KPIs') },
    { key: 'topPatientKpis', label: tr('Top usagers KPI (%)', 'Top patient KPIs (%)') },
    { key: 'workHoursTop10', label: tr('Heures travaillées (Top 10)', 'Work hours (Top 10)') },
    { key: 'timeCenterTop10', label: tr('Temps au centre (Top 10)', 'Time spent at center (Top 10)') },
    { key: 'timeCenterStats', label: tr('Statistiques Temps au Centre', 'Time at center statistics') },
    { key: 'lateness', label: tr('Retards employés', 'Employee lateness') },
    { key: 'operations', label: tr('Opérations', 'Operations') },
    { key: 'safety', label: tr('Sécurité', 'Safety') },
    { key: 'estimatedCosts', label: tr('Détail des coûts estimés', 'Estimated cost breakdown') },
    { key: 'staffLoad', label: tr('Charge du personnel', 'Staff workload') },
    { key: 'patientKpiTable', label: tr('KPI par usager', 'KPI by patient') },
    { key: 'incidentsToProcess', label: tr('Incidents à traiter', 'Incidents to process') },
    { key: 'timesheets', label: t('reports.timesheets') },
  ];

  const load = async () => {
    if (isUsager) {
      setLoading(true);
      try {
        const [patientKpiRes, tsRes] = await Promise.all([
          reportsAPI.getPatientKpis({
            start: startDateTime,
            end: endDateTime,
            patient_id: user?.id,
          }),
          reportsAPI.getTimesheets({
            start: startDateTime,
            end: endDateTime,
          }),
        ]);
        setPatientKpis(patientKpiRes.data?.items || []);
        setTimesheets(tsRes.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const [tsRes, sumRes, opsRes, safetyRes, kpiRes, patientKpiRes] = await Promise.all([
        reportsAPI.getTimesheets({ start: startDateTime, end: endDateTime }),
        reportsAPI.getSummary({ start: startDateTime, end: endDateTime }),
        reportsAPI.getOperations(reportParams),
        reportsAPI.getSafety(reportParams),
        reportsAPI.getKpis(reportParams),
        reportsAPI.getPatientKpis(reportParams),
      ]);
      setTimesheets(tsRes.data || []);
      setSummary(sumRes.data);
      setOperations(opsRes.data || null);
      setSafety(safetyRes.data || null);
      setKpis(kpiRes.data || null);
      setPatientKpis(patientKpiRes.data?.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [startDateTime, endDateTime, isUsager, user?.id]);

  const timesheetEmployeeOptions = useMemo(() => {
    const values = new Set();
    (timesheets || []).forEach((e) => {
      const v = `${e?.first_name || ''} ${e?.last_name || ''}`.trim();
      if (v) values.add(v);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [timesheets]);

  const timesheetNoOptions = useMemo(() => {
    const values = new Set();
    (timesheets || []).forEach((e) => {
      const v = String(e?.employee_number || '').trim();
      if (v) values.add(v);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [timesheets]);

  const timesheetDateOptions = useMemo(() => {
    const values = new Set();
    (timesheets || []).forEach((e) => {
      const v = String(e?.work_date || '').trim();
      if (v) values.add(v);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [timesheets]);

  const timesheetEntryOptions = useMemo(() => {
    const values = new Set();
    (timesheets || []).forEach((e) => {
      if (e?.clock_in) values.add(format(new Date(e.clock_in), 'HH:mm'));
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [timesheets]);

  const timesheetExitOptions = useMemo(() => {
    const values = new Set();
    (timesheets || []).forEach((e) => {
      if (e?.clock_out) values.add(format(new Date(e.clock_out), 'HH:mm'));
      else values.add(tr('En cours', 'In progress'));
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [timesheets, tr]);

  const timesheetHoursOptions = useMemo(() => {
    const values = new Set();
    (timesheets || []).forEach((e) => {
      values.add(`${parseFloat(e?.hours_worked || 0).toFixed(2)}h`);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [timesheets]);

  const timesheetMinutesOptions = useMemo(() => {
    const values = new Set();
    (timesheets || []).forEach((e) => {
      values.add(`${Math.round(Number(e?.hours_worked || 0) * 60)} min`);
    });
    return Array.from(values).sort((a, b) => Number.parseInt(a, 10) - Number.parseInt(b, 10));
  }, [timesheets]);

  const timesheetCenterOptions = useMemo(() => {
    const values = new Set();
    (timesheets || []).forEach((e) => {
      const v = String(e?.center_name || '').trim();
      if (v) values.add(v);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [timesheets]);

  const filteredTimesheets = useMemo(() => {
    return (timesheets || []).filter((e) => {
      if (tsCenterFilter !== 'all' && String(e?.center_name || '') !== tsCenterFilter) return false;

      const employeeValue = `${e?.first_name || ''} ${e?.last_name || ''}`.trim();
      const noValue = String(e?.employee_number || '');
      const dateValue = String(e?.work_date || '');
      const entryValue = e?.clock_in ? format(new Date(e.clock_in), 'HH:mm') : '';
      const exitValue = e?.clock_out ? format(new Date(e.clock_out), 'HH:mm') : tr('En cours', 'In progress');
      const hoursValue = `${parseFloat(e?.hours_worked || 0).toFixed(2)}h`;
      const minutesValue = `${Math.round(Number(e?.hours_worked || 0) * 60)} min`;

      if (tsEmployeeFilter !== 'all' && employeeValue !== tsEmployeeFilter) return false;
      if (tsNoFilter !== 'all' && noValue !== tsNoFilter) return false;
      if (tsDateFilter !== 'all' && dateValue !== tsDateFilter) return false;
      if (tsEntryFilter !== 'all' && entryValue !== tsEntryFilter) return false;
      if (tsExitFilter !== 'all' && exitValue !== tsExitFilter) return false;
      if (tsHoursFilter !== 'all' && hoursValue !== tsHoursFilter) return false;
      if (tsMinutesFilter !== 'all' && minutesValue !== tsMinutesFilter) return false;
      return true;
    });
  }, [timesheets, tsEmployeeFilter, tsNoFilter, tsDateFilter, tsEntryFilter, tsExitFilter, tsHoursFilter, tsMinutesFilter, tsCenterFilter, tr]);

  const staffLoadRows = operations?.staffLoad || [];
  const [staffEmployeeFilter, setStaffEmployeeFilter] = useState('all');
  const [staffNoFilter, setStaffNoFilter] = useState('all');
  const [staffFunctionFilter, setStaffFunctionFilter] = useState('all');
  const [staffHoursFilter, setStaffHoursFilter] = useState('all');
  const staffEmployeeOptions = useMemo(() => {
    const values = new Set(staffLoadRows.map((s) => `${s?.first_name || ''} ${s?.last_name || ''}`.trim()).filter(Boolean));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [staffLoadRows]);
  const staffNoOptions = useMemo(() => {
    const values = new Set(staffLoadRows.map((s) => String(s?.employee_number || '').trim()).filter(Boolean));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [staffLoadRows]);
  const staffFunctionOptions = useMemo(() => {
    const values = new Set(staffLoadRows.map((s) => String(s?.job_function || '—').trim()));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [staffLoadRows]);
  const staffHoursOptions = useMemo(() => {
    const values = new Set(staffLoadRows.map((s) => `${Number(s?.hours_worked || 0)}h`));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [staffLoadRows]);
  const filteredStaffLoadRows = useMemo(() => (
    staffLoadRows.filter((s) => {
      const employee = `${s?.first_name || ''} ${s?.last_name || ''}`.trim();
      const no = String(s?.employee_number || '').trim();
      const fn = String(s?.job_function || '—').trim();
      const hours = `${Number(s?.hours_worked || 0)}h`;
      if (staffEmployeeFilter !== 'all' && employee !== staffEmployeeFilter) return false;
      if (staffNoFilter !== 'all' && no !== staffNoFilter) return false;
      if (staffFunctionFilter !== 'all' && fn !== staffFunctionFilter) return false;
      if (staffHoursFilter !== 'all' && hours !== staffHoursFilter) return false;
      return true;
    })
  ), [staffLoadRows, staffEmployeeFilter, staffNoFilter, staffFunctionFilter, staffHoursFilter]);

  const [kpiUserFilter, setKpiUserFilter] = useState('all');
  const [kpiSocialFilter, setKpiSocialFilter] = useState('all');
  const [kpiAutonomyFilter, setKpiAutonomyFilter] = useState('all');
  const [kpiFamilyFilter, setKpiFamilyFilter] = useState('all');
  const [kpiFallsFilter, setKpiFallsFilter] = useState('all');
  const [kpiParticipationFilter, setKpiParticipationFilter] = useState('all');
  const [kpiHospitalFilter, setKpiHospitalFilter] = useState('all');
  const [kpiFollowupFilter, setKpiFollowupFilter] = useState('all');
  const [kpiMealFilter, setKpiMealFilter] = useState('all');
  const [kpiPresenceFilter, setKpiPresenceFilter] = useState('all');
  const kpiUserOptions = useMemo(() => {
    const values = new Set((patientKpis || []).map((p) => `${p?.first_name || ''} ${p?.last_name || ''}`.trim()).filter(Boolean));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [patientKpis]);
  const kpiValueOptions = (key) => {
    const values = new Set((patientKpis || []).map((p) => `${Number(p?.[key] || 0).toFixed(1)}%`));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  };
  const kpiFallsOptions = useMemo(() => {
    const values = new Set((patientKpis || []).map((p) => String(p?.falls_count || 0)));
    return Array.from(values).sort((a, b) => Number(a) - Number(b));
  }, [patientKpis]);
  const filteredPatientKpis = useMemo(() => (
    (patientKpis || []).filter((p) => {
      const userName = `${p?.first_name || ''} ${p?.last_name || ''}`.trim();
      const social = `${Number(p?.socialization_rate_percent || 0).toFixed(1)}%`;
      const autonomy = `${Number(p?.autonomy_progress_rate_percent || 0).toFixed(1)}%`;
      const family = `${Number(p?.family_caregiver_satisfaction_rate_percent || 0).toFixed(1)}%`;
      const falls = String(p?.falls_count || 0);
      const participation = `${Number(p?.user_activity_participation_rate_percent || 0).toFixed(1)}%`;
      const hospital = `${Number(p?.hospitalization_reduction_rate_percent || 0).toFixed(1)}%`;
      const followup = `${Number(p?.care_followup_rate_percent || 0).toFixed(1)}%`;
      const meal = `${Number(p?.meal_satisfaction_rate_percent || 0).toFixed(1)}%`;
      const presence = `${Number(p?.presence_rate_percent || 0).toFixed(1)}%`;
      if (kpiUserFilter !== 'all' && userName !== kpiUserFilter) return false;
      if (kpiSocialFilter !== 'all' && social !== kpiSocialFilter) return false;
      if (kpiAutonomyFilter !== 'all' && autonomy !== kpiAutonomyFilter) return false;
      if (kpiFamilyFilter !== 'all' && family !== kpiFamilyFilter) return false;
      if (kpiFallsFilter !== 'all' && falls !== kpiFallsFilter) return false;
      if (kpiParticipationFilter !== 'all' && participation !== kpiParticipationFilter) return false;
      if (kpiHospitalFilter !== 'all' && hospital !== kpiHospitalFilter) return false;
      if (kpiFollowupFilter !== 'all' && followup !== kpiFollowupFilter) return false;
      if (kpiMealFilter !== 'all' && meal !== kpiMealFilter) return false;
      if (kpiPresenceFilter !== 'all' && presence !== kpiPresenceFilter) return false;
      return true;
    })
  ), [
    patientKpis, kpiUserFilter, kpiSocialFilter, kpiAutonomyFilter, kpiFamilyFilter, kpiFallsFilter,
    kpiParticipationFilter, kpiHospitalFilter, kpiFollowupFilter, kpiMealFilter, kpiPresenceFilter,
  ]);

  const unresolvedRows = safety?.unresolvedIncidents || [];
  const [incDateFilter, setIncDateFilter] = useState('all');
  const [incPatientFilter, setIncPatientFilter] = useState('all');
  const [incTypeFilter, setIncTypeFilter] = useState('all');
  const [incSeverityFilter, setIncSeverityFilter] = useState('all');
  const [incStatusFilter, setIncStatusFilter] = useState('all');
  const [incFollowupFilter, setIncFollowupFilter] = useState('all');
  const incidentDateOptions = useMemo(() => {
    const values = new Set(unresolvedRows.map((i) => (i?.occurred_at ? new Date(i.occurred_at).toLocaleDateString(isFr ? 'fr-CA' : 'en-CA') : '—')));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [unresolvedRows, isFr]);
  const incidentPatientOptions = useMemo(() => {
    const values = new Set(unresolvedRows.map((i) => `${i?.patient_first_name || ''} ${i?.patient_last_name || ''}`.trim()).filter(Boolean));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [unresolvedRows]);
  const incidentTypeOptions = useMemo(() => {
    const values = new Set(unresolvedRows.map((i) => String(i?.incident_type || '').trim()).filter(Boolean));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [unresolvedRows]);
  const incidentSeverityOptions = useMemo(() => {
    const values = new Set(unresolvedRows.map((i) => String(i?.severity || '').trim()).filter(Boolean));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [unresolvedRows]);
  const incidentStatusOptions = useMemo(() => {
    const values = new Set(unresolvedRows.map((i) => String(i?.status || '').trim()).filter(Boolean));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [unresolvedRows]);
  const incidentFollowupOptions = useMemo(() => {
    const values = new Set(unresolvedRows.map((i) => String(i?.followup_date || '—').trim()));
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [unresolvedRows]);
  const filteredIncidents = useMemo(() => (
    unresolvedRows.filter((i) => {
      const date = i?.occurred_at ? new Date(i.occurred_at).toLocaleDateString(isFr ? 'fr-CA' : 'en-CA') : '—';
      const patient = `${i?.patient_first_name || ''} ${i?.patient_last_name || ''}`.trim();
      const type = String(i?.incident_type || '').trim();
      const severity = String(i?.severity || '').trim();
      const status = String(i?.status || '').trim();
      const followup = String(i?.followup_date || '—').trim();
      if (incDateFilter !== 'all' && date !== incDateFilter) return false;
      if (incPatientFilter !== 'all' && patient !== incPatientFilter) return false;
      if (incTypeFilter !== 'all' && type !== incTypeFilter) return false;
      if (incSeverityFilter !== 'all' && severity !== incSeverityFilter) return false;
      if (incStatusFilter !== 'all' && status !== incStatusFilter) return false;
      if (incFollowupFilter !== 'all' && followup !== incFollowupFilter) return false;
      return true;
    })
  ), [unresolvedRows, isFr, incDateFilter, incPatientFilter, incTypeFilter, incSeverityFilter, incStatusFilter, incFollowupFilter]);

  const submitSurvey = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    setSurveySubmitting(true);
    setSurveyMessage('');
    try {
      await kpiAPI.createSurvey({
        surveyType,
        patientId: user.id,
        rating: Number(surveyRating),
        comment: surveyComment || null,
        submittedAt: new Date().toISOString(),
      });
      setSurveyComment('');
      setSurveyMessage(tr('Sondage enregistré.', 'Survey submitted.'));
    } catch (err) {
      setSurveyMessage(err?.response?.data?.message || tr('Erreur lors de l’envoi du sondage.', 'Error while submitting survey.'));
    } finally {
      setSurveySubmitting(false);
    }
  };

  if (isUsager) {
    const uk = patientKpis?.[0] || null;
    const usagerScans = (timesheets || []).filter((e) => isBadgeScanEntry(e));
    const usagerSessions = usagerScans.length;
    const usagerTotalHours = usagerScans.reduce((sum, e) => sum + Number(e.hours_worked || 0), 0);
    const usagerCompleted = usagerScans.filter((e) => !!e.clock_out).length;
    const usagerInProgress = usagerScans.filter((e) => !e.clock_out).length;
    const usagerCardHeaderStyle = {
      background: 'linear-gradient(90deg, rgba(13,148,136,0.22) 0%, rgba(13,148,136,0.12) 100%)',
      borderBottom: '1px solid rgba(13,148,136,0.28)',
      borderLeft: '4px solid rgba(13,148,136,0.9)',
      color: '#0f172a',
    };

    return (
      <div>
        <div className="page-header">
          <div>
            <h2>{t('reports.title')}</h2>
            <p className="text-muted">{tr('Section Sondage et indicateurs', 'Survey and indicators section')}</p>
          </div>
        </div>

        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div className="flex gap-4 items-center flex-wrap">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('reports.startDate')}</label>
              <input type="datetime-local" className="form-input" value={startDateTime} onChange={e => setStartDateTime(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('reports.endDate')}</label>
              <input type="datetime-local" className="form-input" value={endDateTime} onChange={e => setEndDateTime(e.target.value)} />
            </div>
            <div style={{ marginTop: 20 }}>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setStartDateTime(toDateTimeLocal(startOfMonth(new Date())));
                  setEndDateTime(toDateTimeLocal(endOfMonth(new Date())));
                }}
              >
                {tr('Mois actuel', 'Current month')}
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="card" style={{ marginBottom: 20, padding: 16 }}>
            {tr('Chargement des indicateurs...', 'Loading indicators...')}
          </div>
        )}

        {!loading && (
          <div className="grid-2" style={{ gap: 24, marginBottom: 20 }}>
            <div className="card">
              <div className="card-header" style={usagerCardHeaderStyle}>
                <span className="card-title">🎯 {tr('KPI Globaux', 'Global KPIs')}</span>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.presenceRate')}</div>
                    <strong>{Number(uk?.presence_rate_percent || 0).toFixed(2)}%</strong>
                  </div>
                  <div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.activityParticipationRate')}</div>
                    <strong>{Number(uk?.user_activity_participation_rate_percent || 0).toFixed(2)}%</strong>
                  </div>
                  <div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.socializationRate')}</div>
                    <strong>{Number(uk?.socialization_rate_percent || 0).toFixed(2)}%</strong>
                  </div>
                  <div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.autonomyProgressRate')}</div>
                    <strong>{Number(uk?.autonomy_progress_rate_percent || 0).toFixed(2)}%</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header" style={usagerCardHeaderStyle}>
                <span className="card-title">🛡️ {tr('Sécurité', 'Safety')}</span>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.fallsCount')}</div>
                    <strong>{Number(uk?.falls_count || 0)}</strong>
                  </div>
                  <div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.careFollowupRate')}</div>
                    <strong>{Number(uk?.care_followup_rate_percent || 0).toFixed(2)}%</strong>
                  </div>
                  <div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Hospitalisations (période)', 'Hospitalizations (period)')}</div>
                    <strong>{Number(uk?.hospitalizations_current_period || 0)}</strong>
                  </div>
                  <div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.hospitalizationReductionRate')}</div>
                    <strong>{Number(uk?.hospitalization_reduction_rate_percent || 0).toFixed(2)}%</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <>
            <div className="stats-grid" style={{ marginBottom: 20 }}>
              <div className="stat-card">
                <div className="stat-icon">🔁</div>
                <div className="stat-value">{usagerSessions}</div>
                <div className="stat-label">{tr('Sessions IN/OUT', 'IN/OUT sessions')}</div>
              </div>
              <div className="stat-card success">
                <div className="stat-icon">⏱️</div>
                <div className="stat-value">{usagerTotalHours.toFixed(2)}h</div>
                <div className="stat-label">{tr('Temps total', 'Total time')}</div>
              </div>
              <div className="stat-card info">
                <div className="stat-icon">✅</div>
                <div className="stat-value">{usagerCompleted}</div>
                <div className="stat-label">{tr('Sessions complétées', 'Completed sessions')}</div>
              </div>
              <div className="stat-card warning">
                <div className="stat-icon">🟡</div>
                <div className="stat-value">{usagerInProgress}</div>
                <div className="stat-label">{tr('Sessions en cours', 'Sessions in progress')}</div>
              </div>
            </div>

            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header" style={usagerCardHeaderStyle}>
                <span className="card-title">📋 {tr('Rapport de Pointage', 'Timeclock Report')}</span>
                <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>{usagerScans.length} {tr('entrées', 'entries')}</span>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>{tr('Date', 'Date')}</th>
                      <th>{t('timeclock.entry')}</th>
                      <th>{t('timeclock.exit')}</th>
                      <th>{t('reports.totalHours')}</th>
                      <th>{tr('Total (min)', 'Total (min)')}</th>
                      <th>{tr('Centre', 'Center')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!usagerScans.length && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 24 }}>
                          {t('common.noData')}
                        </td>
                      </tr>
                    )}
                    {usagerScans.map((e, i) => (
                      <tr key={`${e.user_id}-${e.clock_in}-${i}`}>
                        <td>{e.work_date ? format(new Date(e.work_date), 'yyyy-MM-dd') : '—'}</td>
                        <td>{e.clock_in ? format(new Date(e.clock_in), 'HH:mm') : '—'}</td>
                        <td>{e.clock_out ? format(new Date(e.clock_out), 'HH:mm') : <span className="status-badge status-active">{tr('En cours', 'In progress')}</span>}</td>
                        <td><strong>{Number(e.hours_worked || 0).toFixed(2)}h</strong></td>
                        <td><strong>{Math.round(Number(e.hours_worked || 0) * 60)} min</strong></td>
                        <td>{e.center_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <div className="card card-form" style={{ maxWidth: 860 }}>
          <h3 style={{ marginBottom: 12 }}>{tr('Sondage usager', 'User survey')}</h3>
          <form onSubmit={submitSurvey}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Type de sondage', 'Survey type')}</label>
                <select className="form-select" value={surveyType} onChange={(e) => setSurveyType(e.target.value)}>
                  <option value="meal">{tr('Satisfaction repas', 'Meal satisfaction')}</option>
                  <option value="family_caregiver">{tr('Satisfaction proche aidant', 'Family/caregiver satisfaction')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Note (1 à 5)', 'Rating (1 to 5)')}</label>
                <select className="form-select" value={surveyRating} onChange={(e) => setSurveyRating(e.target.value)}>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Commentaire (optionnel)', 'Comment (optional)')}</label>
              <textarea
                className="form-textarea"
                rows={4}
                value={surveyComment}
                onChange={(e) => setSurveyComment(e.target.value)}
                placeholder={tr('Votre commentaire...', 'Your comment...')}
              />
            </div>
            {surveyMessage && (
              <div className="alert alert-info" style={{ marginBottom: 12 }}>{surveyMessage}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="btn btn-primary" disabled={surveySubmitting}>
                {surveySubmitting ? tr('Envoi...', 'Submitting...') : tr('Envoyer le sondage', 'Submit survey')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const openCsvOptions = () => {
    setCsvStartDateTime(startDateTime);
    setCsvEndDateTime(endDateTime);
    setShowCsvOptions(true);
  };

  const fetchReportBundle = async (start, end) => {
    const params = isManager
      ? { start, end }
      : { start, end, center_id: undefined };
    const [tsRes, sumRes, opsRes, safetyRes, kpiRes, patientKpiRes] = await Promise.all([
      reportsAPI.getTimesheets({ start, end }),
      reportsAPI.getSummary({ start, end }),
      reportsAPI.getOperations(params),
      reportsAPI.getSafety(params),
      reportsAPI.getKpis(params),
      reportsAPI.getPatientKpis(params),
    ]);

    const ts = tsRes.data || [];
    const sum = sumRes.data || {};
    const ops = opsRes.data || {};
    const safe = safetyRes.data || {};
    const kpiObj = kpiRes.data?.kpis || {};
    const patientKpis = patientKpiRes.data?.items || [];

    const scanned = ts.filter((e) => isBadgeScanEntry(e));
    const scanMap = scanned.reduce((acc, row) => {
      if (!acc[row.user_id]) {
        acc[row.user_id] = {
          user_id: row.user_id,
          employee_number: row.employee_number,
          first_name: row.first_name,
          last_name: row.last_name,
          center_name: row.center_name,
          sessions: 0,
          total_hours: 0,
        };
      }
      acc[row.user_id].sessions += 1;
      acc[row.user_id].total_hours += Number(row.hours_worked || 0);
      return acc;
    }, {});

    const scanPeople = Object.values(scanMap).sort((a, b) => b.total_hours - a.total_hours);
    const scanTotalHours = scanPeople.reduce((sumH, p) => sumH + p.total_hours, 0);

    return {
      timesheets: ts,
      summary: sum,
      operations: ops,
      safety: safe,
      kpis: kpiObj,
      patientKpis,
      scanPeople,
      scanStats: {
        distinctPeople: scanPeople.length,
        totalSessions: scanned.length,
        totalHours: scanTotalHours,
        avgByPerson: scanPeople.length ? (scanTotalHours / scanPeople.length) : 0,
        avgBySession: scanned.length ? (scanTotalHours / scanned.length) : 0,
        completed: scanned.filter((r) => !!r.clock_out).length,
        inProgress: scanned.filter((r) => !r.clock_out).length,
      },
    };
  };

  const openPdfOptions = () => {
    setPdfStartDateTime(startDateTime);
    setPdfEndDateTime(endDateTime);
    setShowPdfOptions(true);
  };

  const csvEscape = (value) => {
    const str = String(value ?? '');
    if (str.includes('"') || str.includes(',') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const csvLine = (arr) => arr.map(csvEscape).join(',');

  const buildCsvContent = (bundle, start, end, selectedBlocks) => {
    const lines = [];
    const pushSection = (title, headers = [], rows = []) => {
      lines.push(csvLine([title]));
      if (headers.length) lines.push(csvLine(headers));
      rows.forEach((row) => lines.push(csvLine(row)));
      lines.push('');
    };
    const dateFmt = (value) => (value ? new Date(value).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—');

    lines.push(csvLine([`${t('reports.title')} - CSV`]));
    lines.push(csvLine([`${tr('Période', 'Period')}: ${dateFmt(start)} -> ${dateFmt(end)}`]));
    lines.push('');

    if (selectedBlocks.includes('overviewStats')) {
      const totalHoursPdf = bundle.timesheets.reduce((s, e) => s + Number(e.hours_worked || 0), 0);
      pushSection(tr('Résumé statistique', 'Summary statistics'), [tr('Indicateur', 'Metric'), tr('Valeur', 'Value')], [
        [t('reports.totalHours'), `${totalHoursPdf.toFixed(1)}h`],
        [tr('Employés actifs', 'Active employees'), bundle.summary?.hoursByStaff?.length || 0],
        [tr('Incidents non résolus', 'Unresolved incidents'), bundle.safety?.overview?.unresolved_incidents || 0],
      ]);
    }

    if (selectedBlocks.includes('globalKpis')) {
      const k = bundle.kpis || {};
      pushSection(tr('KPI globaux', 'Global KPIs'), [tr('Indicateur', 'Metric'), tr('Valeur', 'Value')], [
        [t('reports.kpi.socializationRate'), `${Number(k.socialization_rate_percent || 0).toFixed(2)}%`],
        [t('reports.kpi.autonomyProgressRate'), `${Number(k.autonomy_progress_rate_percent || 0).toFixed(2)}%`],
        [t('reports.kpi.familySatisfactionRate'), `${Number(k.family_caregiver_satisfaction_rate_percent || 0).toFixed(2)}%`],
        [t('reports.kpi.fallsCount'), k.falls_count || 0],
        [t('reports.kpi.activityParticipationRate'), `${Number(k.user_activity_participation_rate_percent || 0).toFixed(2)}%`],
        [t('reports.kpi.activityFrequencyPerPatient'), Number(k.user_activity_frequency_per_patient || 0).toFixed(2)],
        [t('reports.kpi.hospitalizationReductionRate'), `${Number(k.hospitalization_reduction_rate_percent || 0).toFixed(2)}%`],
        [t('reports.kpi.careFollowupRate'), `${Number(k.care_followup_rate_percent || 0).toFixed(2)}%`],
        [t('reports.kpi.mealSatisfactionRate'), `${Number(k.meal_satisfaction_rate_percent || 0).toFixed(2)}%`],
        [t('reports.kpi.presenceRate'), `${Number(k.presence_rate_percent || 0).toFixed(2)}%`],
      ]);
    }

    if (selectedBlocks.includes('patientKpisOverview')) {
      const items = bundle.patientKpis || [];
      const avg = (getter) => (items.length ? items.reduce((sum, it) => sum + Number(getter(it) || 0), 0) / items.length : 0);
      pushSection(tr('KPI usagers', 'Patient KPIs'), [tr('Indicateur', 'Metric'), tr('Valeur', 'Value')], [
        [tr('Nombre d’usagers', 'Number of patients'), items.length],
        [t('reports.kpi.presenceRate'), `${avg((p) => p.presence_rate_percent).toFixed(2)}%`],
        [t('reports.kpi.activityParticipationRate'), `${avg((p) => p.user_activity_participation_rate_percent).toFixed(2)}%`],
        [t('reports.kpi.socializationRate'), `${avg((p) => p.socialization_rate_percent).toFixed(2)}%`],
        [t('reports.kpi.autonomyProgressRate'), `${avg((p) => p.autonomy_progress_rate_percent).toFixed(2)}%`],
        [t('reports.kpi.fallsCount'), avg((p) => p.falls_count).toFixed(2)],
      ]);
    }

    if (selectedBlocks.includes('topPatientKpis')) {
      const top = (bundle.patientKpis || [])
        .slice()
        .sort((a, b) => Number(b.presence_rate_percent || 0) - Number(a.presence_rate_percent || 0))
        .slice(0, 10)
        .map((p) => [
          `${p.first_name || ''} ${p.last_name || ''}`,
          `${Number(p.presence_rate_percent || 0).toFixed(1)}%`,
          `${Number(p.user_activity_participation_rate_percent || 0).toFixed(1)}%`,
          `${Number(p.socialization_rate_percent || 0).toFixed(1)}%`,
        ]);
      pushSection(
        tr('Top usagers KPI (%)', 'Top patient KPIs (%)'),
        [tr('Usager', 'Patient'), t('reports.kpi.presenceRate'), t('reports.kpi.activityParticipationRate'), t('reports.kpi.socializationRate')],
        top
      );
    }

    if (selectedBlocks.includes('workHoursTop10')) {
      const top = (bundle.summary?.hoursByStaff || []).slice(0, 10).map((s) => [
        `${s.first_name || ''} ${s.last_name || ''}`,
        s.employee_number || '—',
        `${Number(s.total_hours || 0).toFixed(2)}h`,
      ]);
      pushSection(tr('Heures travaillées (Top 10)', 'Work hours (Top 10)'), [t('reports.employee'), 'No.', t('reports.totalHours')], top);
    }

    if (selectedBlocks.includes('timeCenterTop10')) {
      const top = bundle.scanPeople.slice(0, 10).map((s) => [
        `${s.first_name || ''} ${s.last_name || ''}`,
        s.employee_number || '—',
        `${Number(s.total_hours || 0).toFixed(2)}h`,
      ]);
      pushSection(tr('Temps au centre (Top 10)', 'Time spent at center (Top 10)'), [t('reports.employee'), 'No.', tr('Temps total', 'Total time')], top);
    }

    if (selectedBlocks.includes('timeCenterStats')) {
      const st = bundle.scanStats;
      pushSection(tr('Statistiques Temps au Centre', 'Time at center statistics'), [tr('Indicateur', 'Metric'), tr('Valeur', 'Value')], [
        [tr('Personnes ayant scanné', 'People who scanned'), st.distinctPeople],
        [tr('Sessions IN/OUT', 'IN/OUT sessions'), st.totalSessions],
        [tr('Temps total', 'Total time'), `${st.totalHours.toFixed(2)}h`],
        [tr('Moyenne par personne', 'Average per person'), `${st.avgByPerson.toFixed(2)}h`],
        [tr('Moyenne par session', 'Average per session'), `${st.avgBySession.toFixed(2)}h`],
        [tr('Sessions complétées', 'Completed sessions'), `${st.completed} (${tr('En cours', 'In progress')}: ${st.inProgress})`],
      ]);
    }

    if (selectedBlocks.includes('lateness')) {
      const rows = (bundle.operations?.lateness?.late_employees || []).slice(0, 100).map((r) => [
        `${r.first_name || ''} ${r.last_name || ''}`,
        r.employee_number || '—',
        r.start_time ? new Date(r.start_time).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—',
        r.first_clock_in ? new Date(r.first_clock_in).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—',
        r.late_minutes || 0,
      ]);
      pushSection(tr('Retards employés', 'Employee lateness'), [tr('Employé', 'Employee'), 'No.', tr('Début quart', 'Shift start'), tr('Pointage', 'Clock-in'), tr('Retard (min)', 'Delay (min)')], rows);
    }

    if (selectedBlocks.includes('operations')) {
      const op = bundle.operations || {};
      pushSection(tr('Opérations', 'Operations'), [tr('Indicateur', 'Metric'), tr('Valeur', 'Value')], [
        [tr('Inscriptions activités', 'Activity registrations'), op.attendance?.total_registrations || 0],
        [tr('Présences', 'Attendances'), op.attendance?.total_attended || 0],
        [tr("Liste d'attente", 'Waitlist'), op.attendance?.total_waitlisted || 0],
        [tr('Trajets complétés', 'Completed trips'), op.transport?.completed_trips || 0],
        [tr('Trajets annulés', 'Cancelled trips'), op.transport?.cancelled_trips || 0],
        [tr('Retard moyen (min)', 'Average delay (min)'), op.transport?.avg_delay_minutes || 0],
      ]);
    }

    if (selectedBlocks.includes('safety')) {
      const sf = bundle.safety || {};
      pushSection(tr('Sécurité', 'Safety'), [tr('Indicateur', 'Metric'), tr('Valeur', 'Value')], [
        [tr('Incidents total', 'Total incidents'), sf.overview?.total_incidents || 0],
        [tr('Chutes', 'Falls'), sf.overview?.total_falls || 0],
        [tr('Suivis requis', 'Required follow-ups'), sf.overview?.followup_required || 0],
        [tr('Suivis en retard', 'Overdue follow-ups'), sf.overview?.overdue_followups || 0],
      ]);
    }

    if (selectedBlocks.includes('estimatedCosts')) {
      const costs = bundle.operations?.costs || {};
      const transport = bundle.operations?.transport || {};
      pushSection(tr('Détail des coûts estimés', 'Estimated cost breakdown'), [tr('Indicateur', 'Metric'), tr('Valeur', 'Value')], [
        [tr('Coût horaire personnel', 'Staff hourly cost'), `${Number(costs.staff_hourly_cost || 0).toFixed(2)}$ / h`],
        [tr('Coût unitaire transport', 'Transport unit cost'), `${Number(costs.transport_trip_cost || 0).toFixed(2)}$ / ${tr('trajet', 'trip')}`],
        [tr('Heures totales personnel', 'Total staff hours'), `${Number(costs.total_staff_hours || 0).toFixed(2)} h`],
        [tr('Trajets complétés', 'Completed trips'), transport.completed_trips || 0],
        [tr('Coût personnel estimé', 'Estimated staff cost'), `${Number(costs.estimated_staff_cost || 0).toFixed(2)}$`],
        [tr('Coût transport estimé', 'Estimated transport cost'), `${Number(costs.estimated_transport_cost || 0).toFixed(2)}$`],
      ]);
    }

    if (selectedBlocks.includes('staffLoad')) {
      const rows = (bundle.operations?.staffLoad || []).slice(0, 200).map((s) => [
        `${s.first_name || ''} ${s.last_name || ''}`,
        s.employee_number || '—',
        s.job_function || '—',
        `${Number(s.hours_worked || 0).toFixed(2)}h`,
      ]);
      pushSection(tr('Charge du personnel', 'Staff workload'), [tr('Employé', 'Employee'), 'No.', tr('Fonction', 'Function'), tr('Heures', 'Hours')], rows);
    }

    if (selectedBlocks.includes('patientKpiTable')) {
      const rows = (bundle.patientKpis || []).slice(0, 500).map((p) => [
        `${p.first_name || ''} ${p.last_name || ''}`,
        `${Number(p.socialization_rate_percent || 0).toFixed(1)}%`,
        `${Number(p.autonomy_progress_rate_percent || 0).toFixed(1)}%`,
        `${Number(p.family_caregiver_satisfaction_rate_percent || 0).toFixed(1)}%`,
        p.falls_count || 0,
        `${Number(p.user_activity_participation_rate_percent || 0).toFixed(1)}%`,
        `${Number(p.hospitalization_reduction_rate_percent || 0).toFixed(1)}%`,
        `${Number(p.care_followup_rate_percent || 0).toFixed(1)}%`,
        `${Number(p.meal_satisfaction_rate_percent || 0).toFixed(1)}%`,
        `${Number(p.presence_rate_percent || 0).toFixed(1)}%`,
      ]);
      pushSection(tr('KPI par usager', 'KPI by patient'), [
        tr('Usager', 'Patient'),
        t('reports.kpi.socializationRate'),
        t('reports.kpi.autonomyProgressRate'),
        t('reports.kpi.familySatisfactionRate'),
        t('reports.kpi.fallsCount'),
        t('reports.kpi.activityParticipationRate'),
        t('reports.kpi.hospitalizationReductionRate'),
        t('reports.kpi.careFollowupRate'),
        t('reports.kpi.mealSatisfactionRate'),
        t('reports.kpi.presenceRate'),
      ], rows);
    }

    if (selectedBlocks.includes('incidentsToProcess')) {
      const rows = (bundle.safety?.unresolvedIncidents || []).slice(0, 500).map((i) => [
        i.occurred_at ? new Date(i.occurred_at).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—',
        `${i.patient_first_name || ''} ${i.patient_last_name || ''}`,
        i.incident_type || '—',
        i.severity || '—',
        i.status || '—',
        i.followup_date || '—',
      ]);
      pushSection(tr('Incidents à traiter', 'Incidents to process'), [tr('Date', 'Date'), tr('Usager', 'Patient'), tr('Type', 'Type'), tr('Sévérité', 'Severity'), tr('Statut', 'Status'), tr('Suivi', 'Follow-up')], rows);
    }

    if (selectedBlocks.includes('timesheets')) {
      const rows = bundle.timesheets.slice(0, 2000).map((e) => [
        `${e.first_name || ''} ${e.last_name || ''}`,
        e.employee_number || '—',
        e.work_date || '—',
        e.clock_in ? format(new Date(e.clock_in), 'HH:mm') : '—',
        e.clock_out ? format(new Date(e.clock_out), 'HH:mm') : tr('En cours', 'In progress'),
        `${Number(e.hours_worked || 0).toFixed(2)}h`,
        `${Math.round(Number(e.hours_worked || 0) * 60)} min`,
        e.center_name || '—',
      ]);
      pushSection(t('reports.timesheets'), [t('reports.employee'), 'No.', tr('Date', 'Date'), t('timeclock.entry'), t('timeclock.exit'), t('reports.totalHours'), tr('Total (min)', 'Total (min)'), tr('Centre', 'Center')], rows);
    }

    return lines.join('\n');
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const buildSimpleBarSvg = (items, color = '#0d9488', options = {}) => {
    const { valueSuffix = 'h', decimals = 2 } = options;
    const data = (items || []).slice(0, 10);
    if (!data.length) return '';

    const width = 920;
    const height = 340;
    const margin = { top: 20, right: 20, bottom: 90, left: 50 };
    const chartW = width - margin.left - margin.right;
    const chartH = height - margin.top - margin.bottom;
    const maxVal = Math.max(...data.map((d) => Number(d.value || 0)), 1);
    const barGap = 16;
    const barW = Math.max(14, (chartW - barGap * (data.length - 1)) / data.length);

    const bars = data.map((d, i) => {
      const val = Number(d.value || 0);
      const h = Math.max(0, (val / maxVal) * chartH);
      const x = margin.left + i * (barW + barGap);
      const y = margin.top + (chartH - h);
      const labelX = x + barW / 2;
      const valY = y - 6;
      const axisY = margin.top + chartH + 14;
      return `
        <rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${barW.toFixed(1)}" height="${h.toFixed(1)}" rx="4" fill="${color}" />
        <text x="${labelX.toFixed(1)}" y="${valY.toFixed(1)}" text-anchor="middle" font-size="10" fill="#334155">${val.toFixed(decimals)}${valueSuffix}</text>
        <text x="${labelX.toFixed(1)}" y="${axisY.toFixed(1)}" text-anchor="middle" font-size="10" fill="#475569">${escapeHtml(d.label)}</text>
      `;
    }).join('');

    const grid = [0, 0.25, 0.5, 0.75, 1].map((r) => {
      const y = margin.top + chartH - (chartH * r);
      const v = (maxVal * r).toFixed(1);
      return `
        <line x1="${margin.left}" y1="${y.toFixed(1)}" x2="${(margin.left + chartW).toFixed(1)}" y2="${y.toFixed(1)}" stroke="#e2e8f0" stroke-dasharray="2 3" />
        <text x="${(margin.left - 8).toFixed(1)}" y="${(y + 4).toFixed(1)}" text-anchor="end" font-size="10" fill="#64748b">${v}</text>
      `;
    }).join('');

    return `
      <svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" role="img" aria-label="chart">
        <rect x="0" y="0" width="${width}" height="${height}" fill="#ffffff" />
        ${grid}
        <line x1="${margin.left}" y1="${(margin.top + chartH).toFixed(1)}" x2="${(margin.left + chartW).toFixed(1)}" y2="${(margin.top + chartH).toFixed(1)}" stroke="#94a3b8" />
        ${bars}
      </svg>
    `;
  };

  const buildPdfHtml = (bundle, start, end, selectedBlocks) => {
    const formatDateTime = (value) => (value ? new Date(value).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—');
    const section = (title, content) => `
      <section class="section">
        <h2>${title}</h2>
        ${content}
      </section>
    `;
    const table = (headers, rows, emptyCols = headers.length) => `
      <table>
        <thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
        <tbody>
          ${rows.length ? rows.join('') : `<tr><td colspan="${emptyCols}">${t('common.noData')}</td></tr>`}
        </tbody>
      </table>
    `;

    const parts = [];

    if (selectedBlocks.includes('overviewStats')) {
      const totalHoursPdf = bundle.timesheets.reduce((s, e) => s + Number(e.hours_worked || 0), 0);
      parts.push(section(
        tr('Résumé statistique', 'Summary statistics'),
        `<div class="stats">
          <div class="stat"><div class="label">${t('reports.totalHours')}</div><div class="value">${totalHoursPdf.toFixed(1)}h</div></div>
          <div class="stat"><div class="label">${tr('Employés actifs', 'Active employees')}</div><div class="value">${bundle.summary?.hoursByStaff?.length || 0}</div></div>
          <div class="stat"><div class="label">${tr('Incidents non résolus', 'Unresolved incidents')}</div><div class="value">${bundle.safety?.overview?.unresolved_incidents || 0}</div></div>
        </div>`
      ));
    }

    if (selectedBlocks.includes('globalKpis')) {
      const k = bundle.kpis || {};
      parts.push(section(
        tr('KPI globaux', 'Global KPIs'),
        `<div class="stats">
          <div class="stat"><div class="label">${t('reports.kpi.socializationRate')}</div><div class="value">${Number(k.socialization_rate_percent || 0).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.autonomyProgressRate')}</div><div class="value">${Number(k.autonomy_progress_rate_percent || 0).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.familySatisfactionRate')}</div><div class="value">${Number(k.family_caregiver_satisfaction_rate_percent || 0).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.fallsCount')}</div><div class="value">${k.falls_count || 0}</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.activityParticipationRate')}</div><div class="value">${Number(k.user_activity_participation_rate_percent || 0).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.activityFrequencyPerPatient')}</div><div class="value">${Number(k.user_activity_frequency_per_patient || 0).toFixed(2)}</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.hospitalizationReductionRate')}</div><div class="value">${Number(k.hospitalization_reduction_rate_percent || 0).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.careFollowupRate')}</div><div class="value">${Number(k.care_followup_rate_percent || 0).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.mealSatisfactionRate')}</div><div class="value">${Number(k.meal_satisfaction_rate_percent || 0).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.presenceRate')}</div><div class="value">${Number(k.presence_rate_percent || 0).toFixed(2)}%</div></div>
        </div>`
      ));
    }

    if (selectedBlocks.includes('patientKpisOverview')) {
      const items = bundle.patientKpis || [];
      const avg = (getter) => {
        if (!items.length) return 0;
        return items.reduce((sum, it) => sum + Number(getter(it) || 0), 0) / items.length;
      };
      parts.push(section(
        tr('KPI usagers', 'Patient KPIs'),
        `<div class="stats">
          <div class="stat"><div class="label">${tr('Nombre d’usagers', 'Number of patients')}</div><div class="value">${items.length}</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.presenceRate')}</div><div class="value">${avg((p) => p.presence_rate_percent).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.activityParticipationRate')}</div><div class="value">${avg((p) => p.user_activity_participation_rate_percent).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.socializationRate')}</div><div class="value">${avg((p) => p.socialization_rate_percent).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.autonomyProgressRate')}</div><div class="value">${avg((p) => p.autonomy_progress_rate_percent).toFixed(2)}%</div></div>
          <div class="stat"><div class="label">${t('reports.kpi.fallsCount')}</div><div class="value">${avg((p) => p.falls_count).toFixed(2)}</div></div>
        </div>`
      ));
    }

    if (selectedBlocks.includes('topPatientKpis')) {
      const top = (bundle.patientKpis || [])
        .slice()
        .sort((a, b) => Number(b.presence_rate_percent || 0) - Number(a.presence_rate_percent || 0))
        .slice(0, 10);
      const rows = top.map((p) => `
        <tr>
          <td>${p.first_name || ''} ${p.last_name || ''}</td>
          <td>${Number(p.presence_rate_percent || 0).toFixed(1)}%</td>
          <td>${Number(p.user_activity_participation_rate_percent || 0).toFixed(1)}%</td>
          <td>${Number(p.socialization_rate_percent || 0).toFixed(1)}%</td>
        </tr>
      `);
      const chart = buildSimpleBarSvg(
        top.map((p) => ({
          label: `${String(p.first_name || '').charAt(0)}. ${p.last_name || ''}`.trim(),
          value: Number(p.presence_rate_percent || 0),
        })),
        '#2a9d8f',
        { valueSuffix: '%', decimals: 1 }
      );
      parts.push(section(
        tr('Top usagers KPI (%)', 'Top patient KPIs (%)'),
        `${chart ? `<div class="chart-wrap">${chart}</div>` : ''}${table(
          [tr('Usager', 'Patient'), t('reports.kpi.presenceRate'), t('reports.kpi.activityParticipationRate'), t('reports.kpi.socializationRate')],
          rows,
          4
        )}`
      ));
    }

    if (selectedBlocks.includes('workHoursTop10')) {
      const top = (bundle.summary?.hoursByStaff || []).slice(0, 10);
      const rows = top.map((s) => `
        <tr>
          <td>${s.first_name || ''} ${s.last_name || ''}</td>
          <td>${s.employee_number || '—'}</td>
          <td>${Number(s.total_hours || 0).toFixed(2)}h</td>
        </tr>
      `);
      const chart = buildSimpleBarSvg(
        top.map((s) => ({
          label: `${String(s.first_name || '').charAt(0)}. ${s.last_name || ''}`.trim(),
          value: Number(s.total_hours || 0),
        })),
        '#0d9488'
      );
      parts.push(section(
        tr('Heures travaillées (Top 10)', 'Work hours (Top 10)'),
        `${chart ? `<div class="chart-wrap">${chart}</div>` : ''}${table([t('reports.employee'), 'No.', t('reports.totalHours')], rows, 3)}`
      ));
    }

    if (selectedBlocks.includes('timeCenterTop10')) {
      const top = bundle.scanPeople.slice(0, 10);
      const rows = top.map((s) => `
        <tr>
          <td>${s.first_name || ''} ${s.last_name || ''}</td>
          <td>${s.employee_number || '—'}</td>
          <td>${Number(s.total_hours || 0).toFixed(2)}h</td>
        </tr>
      `);
      const chart = buildSimpleBarSvg(
        top.map((s) => ({
          label: `${String(s.first_name || '').charAt(0)}. ${s.last_name || ''}`.trim(),
          value: Number(s.total_hours || 0),
        })),
        '#2563eb'
      );
      parts.push(section(
        tr('Temps au centre (Top 10)', 'Time spent at center (Top 10)'),
        `${chart ? `<div class="chart-wrap">${chart}</div>` : ''}${table([t('reports.employee'), 'No.', tr('Temps total', 'Total time')], rows, 3)}`
      ));
    }

    if (selectedBlocks.includes('timeCenterStats')) {
      const st = bundle.scanStats;
      parts.push(section(
        tr('Statistiques Temps au Centre', 'Time at center statistics'),
        `<div class="stats">
          <div class="stat"><div class="label">${tr('Personnes ayant scanné', 'People who scanned')}</div><div class="value">${st.distinctPeople}</div></div>
          <div class="stat"><div class="label">${tr('Sessions IN/OUT', 'IN/OUT sessions')}</div><div class="value">${st.totalSessions}</div></div>
          <div class="stat"><div class="label">${tr('Temps total', 'Total time')}</div><div class="value">${st.totalHours.toFixed(2)}h</div></div>
          <div class="stat"><div class="label">${tr('Moyenne par personne', 'Average per person')}</div><div class="value">${st.avgByPerson.toFixed(2)}h</div></div>
          <div class="stat"><div class="label">${tr('Moyenne par session', 'Average per session')}</div><div class="value">${st.avgBySession.toFixed(2)}h</div></div>
          <div class="stat"><div class="label">${tr('Sessions complétées', 'Completed sessions')}</div><div class="value">${st.completed} (${tr('En cours', 'In progress')}: ${st.inProgress})</div></div>
        </div>`
      ));
    }

    if (selectedBlocks.includes('lateness')) {
      const rows = (bundle.operations?.lateness?.late_employees || []).slice(0, 20).map((r) => `
        <tr>
          <td>${r.first_name || ''} ${r.last_name || ''}</td>
          <td>${r.employee_number || '—'}</td>
          <td>${r.start_time ? new Date(r.start_time).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—'}</td>
          <td>${r.first_clock_in ? new Date(r.first_clock_in).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—'}</td>
          <td>${r.late_minutes || 0}</td>
        </tr>
      `);
      parts.push(section(
        tr('Retards employés', 'Employee lateness'),
        table(
          [tr('Employé', 'Employee'), 'No.', tr('Début quart', 'Shift start'), tr('Pointage', 'Clock-in'), tr('Retard (min)', 'Delay (min)')],
          rows,
          5
        )
      ));
    }

    if (selectedBlocks.includes('operations')) {
      const op = bundle.operations || {};
      parts.push(section(
        tr('Opérations', 'Operations'),
        `<div class="stats">
          <div class="stat"><div class="label">${tr('Inscriptions activités', 'Activity registrations')}</div><div class="value">${op.attendance?.total_registrations || 0}</div></div>
          <div class="stat"><div class="label">${tr('Présences', 'Attendances')}</div><div class="value">${op.attendance?.total_attended || 0}</div></div>
          <div class="stat"><div class="label">${tr('Trajets complétés', 'Completed trips')}</div><div class="value">${op.transport?.completed_trips || 0}</div></div>
          <div class="stat"><div class="label">${tr('Absences transport', 'Transport no-show')}</div><div class="value">${Number(op.transport?.no_show_rate_percent || 0).toFixed(1)}%</div></div>
        </div>`
      ));
    }

    if (selectedBlocks.includes('safety')) {
      const sf = bundle.safety || {};
      parts.push(section(
        tr('Sécurité', 'Safety'),
        `<div class="stats">
          <div class="stat"><div class="label">${tr('Incidents total', 'Total incidents')}</div><div class="value">${sf.overview?.total_incidents || 0}</div></div>
          <div class="stat"><div class="label">${tr('Chutes', 'Falls')}</div><div class="value">${sf.overview?.total_falls || 0}</div></div>
          <div class="stat"><div class="label">${tr('Suivis en retard', 'Overdue follow-ups')}</div><div class="value">${sf.overview?.overdue_followups || 0}</div></div>
        </div>`
      ));
    }

    if (selectedBlocks.includes('estimatedCosts')) {
      const costs = bundle.operations?.costs || {};
      const transport = bundle.operations?.transport || {};
      parts.push(section(
        tr('Détail des coûts estimés', 'Estimated cost breakdown'),
        `<div class="stats">
          <div class="stat"><div class="label">${tr('Coût horaire personnel', 'Staff hourly cost')}</div><div class="value">${Number(costs.staff_hourly_cost || 0).toFixed(2)}$ / h</div></div>
          <div class="stat"><div class="label">${tr('Coût unitaire transport', 'Transport unit cost')}</div><div class="value">${Number(costs.transport_trip_cost || 0).toFixed(2)}$ / ${tr('trajet', 'trip')}</div></div>
          <div class="stat"><div class="label">${tr('Heures totales personnel', 'Total staff hours')}</div><div class="value">${Number(costs.total_staff_hours || 0).toFixed(2)} h</div></div>
          <div class="stat"><div class="label">${tr('Trajets complétés', 'Completed trips')}</div><div class="value">${transport.completed_trips || 0}</div></div>
          <div class="stat"><div class="label">${tr('Coût personnel estimé', 'Estimated staff cost')}</div><div class="value">${Number(costs.estimated_staff_cost || 0).toFixed(2)}$</div></div>
          <div class="stat"><div class="label">${tr('Coût transport estimé', 'Estimated transport cost')}</div><div class="value">${Number(costs.estimated_transport_cost || 0).toFixed(2)}$</div></div>
        </div>`
      ));
    }

    if (selectedBlocks.includes('staffLoad')) {
      const rows = (bundle.operations?.staffLoad || []).slice(0, 100).map((s) => `
        <tr>
          <td>${s.first_name || ''} ${s.last_name || ''}</td>
          <td>${s.employee_number || '—'}</td>
          <td>${s.job_function || '—'}</td>
          <td>${Number(s.hours_worked || 0).toFixed(2)}h</td>
        </tr>
      `);
      parts.push(section(
        tr('Charge du personnel', 'Staff workload'),
        table([tr('Employé', 'Employee'), 'No.', tr('Fonction', 'Function'), tr('Heures', 'Hours')], rows, 4)
      ));
    }

    if (selectedBlocks.includes('patientKpiTable')) {
      const rows = (bundle.patientKpis || []).slice(0, 200).map((p) => `
        <tr>
          <td>${p.first_name || ''} ${p.last_name || ''}</td>
          <td>${Number(p.socialization_rate_percent || 0).toFixed(1)}%</td>
          <td>${Number(p.autonomy_progress_rate_percent || 0).toFixed(1)}%</td>
          <td>${Number(p.family_caregiver_satisfaction_rate_percent || 0).toFixed(1)}%</td>
          <td>${p.falls_count || 0}</td>
          <td>${Number(p.user_activity_participation_rate_percent || 0).toFixed(1)}%</td>
          <td>${Number(p.hospitalization_reduction_rate_percent || 0).toFixed(1)}%</td>
          <td>${Number(p.care_followup_rate_percent || 0).toFixed(1)}%</td>
          <td>${Number(p.meal_satisfaction_rate_percent || 0).toFixed(1)}%</td>
          <td>${Number(p.presence_rate_percent || 0).toFixed(1)}%</td>
        </tr>
      `);
      parts.push(section(
        tr('KPI par usager', 'KPI by patient'),
        table(
          [
            tr('Usager', 'Patient'),
            t('reports.kpi.socializationRate'),
            t('reports.kpi.autonomyProgressRate'),
            t('reports.kpi.familySatisfactionRate'),
            t('reports.kpi.fallsCount'),
            t('reports.kpi.activityParticipationRate'),
            t('reports.kpi.hospitalizationReductionRate'),
            t('reports.kpi.careFollowupRate'),
            t('reports.kpi.mealSatisfactionRate'),
            t('reports.kpi.presenceRate'),
          ],
          rows,
          10
        )
      ));
    }

    if (selectedBlocks.includes('incidentsToProcess')) {
      const rows = (bundle.safety?.unresolvedIncidents || []).slice(0, 100).map((i) => `
        <tr>
          <td>${i.occurred_at ? new Date(i.occurred_at).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—'}</td>
          <td>${i.patient_first_name || ''} ${i.patient_last_name || ''}</td>
          <td>${i.incident_type || '—'}</td>
          <td>${i.severity || '—'}</td>
          <td>${i.status || '—'}</td>
          <td>${i.followup_date || '—'}</td>
        </tr>
      `);
      parts.push(section(
        tr('Incidents à traiter', 'Incidents to process'),
        table(
          [tr('Date', 'Date'), tr('Usager', 'Patient'), tr('Type', 'Type'), tr('Sévérité', 'Severity'), tr('Statut', 'Status'), tr('Suivi', 'Follow-up')],
          rows,
          6
        )
      ));
    }

    if (selectedBlocks.includes('timesheets')) {
      const rows = bundle.timesheets.slice(0, 400).map((e) => `
        <tr>
          <td>${e.first_name || ''} ${e.last_name || ''}</td>
          <td>${e.employee_number || '—'}</td>
          <td>${e.work_date || '—'}</td>
          <td>${e.clock_in ? format(new Date(e.clock_in), 'HH:mm') : '—'}</td>
          <td>${e.clock_out ? format(new Date(e.clock_out), 'HH:mm') : tr('En cours', 'In progress')}</td>
          <td>${Number(e.hours_worked || 0).toFixed(2)}h</td>
          <td>${Math.round(Number(e.hours_worked || 0) * 60)} min</td>
          <td>${e.center_name || '—'}</td>
        </tr>
      `);
      parts.push(section(
        t('reports.timesheets'),
        table([t('reports.employee'), 'No.', tr('Date', 'Date'), t('timeclock.entry'), t('timeclock.exit'), t('reports.totalHours'), tr('Total (min)', 'Total (min)'), tr('Centre', 'Center')], rows, 8)
      ));
    }

    return `<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${t('reports.timesheets')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
            h1 { margin: 0 0 4px 0; font-size: 22px; }
            .meta { margin-bottom: 16px; color: #6b7280; font-size: 12px; }
            .section { margin: 16px 0; page-break-inside: avoid; }
            .section h2 { margin: 0 0 8px 0; padding-bottom: 6px; border-bottom: 2px solid #0d9488; font-size: 16px; }
            .chart-wrap { border: 1px solid #e5e7eb; border-radius: 8px; padding: 6px; margin: 8px 0 12px 0; }
            .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
            .stat { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; }
            .stat .label { color: #6b7280; font-size: 11px; margin-bottom: 2px; }
            .stat .value { font-size: 15px; font-weight: 700; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #e5e7eb; padding: 6px; font-size: 11px; text-align: left; }
            th { background: #f3f4f6; }
          </style>
        </head>
        <body>
          <h1>${t('reports.title')} - ${t('reports.timesheets')}</h1>
          <div class="meta">${tr('Période', 'Period')}: ${formatDateTime(start)} → ${formatDateTime(end)}</div>
          ${parts.join('')}
        </body>
      </html>`;
  };

  const exportPointagePdf = async () => {
    const selectedKeys = pdfMode === 'all'
      ? pdfBlockList.map((b) => b.key)
      : pdfBlockList.filter((b) => pdfBlocks[b.key]).map((b) => b.key);
    if (!selectedKeys.length) return;
    setExportingPdf(true);
    try {
      const bundle = await fetchReportBundle(pdfStartDateTime, pdfEndDateTime);
      const html = buildPdfHtml(bundle, pdfStartDateTime, pdfEndDateTime, selectedKeys);
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => {
        w.print();
      }, 300);
      setShowPdfOptions(false);
    } catch (err) {
      console.error(err);
    } finally {
      setExportingPdf(false);
    }
  };

  const exportPointageCsv = async () => {
    const selectedKeys = csvMode === 'all'
      ? pdfBlockList.map((b) => b.key)
      : pdfBlockList.filter((b) => csvBlocks[b.key]).map((b) => b.key);
    if (!selectedKeys.length) return;
    setExportingCsv(true);
    try {
      const bundle = await fetchReportBundle(csvStartDateTime, csvEndDateTime);
      const csv = buildCsvContent(bundle, csvStartDateTime, csvEndDateTime, selectedKeys);
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const fileStart = csvStartDateTime.replace(/[:T]/g, '-');
      const fileEnd = csvEndDateTime.replace(/[:T]/g, '-');
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_${fileStart}_${fileEnd}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setShowCsvOptions(false);
    } catch (err) {
      console.error(err);
    } finally {
      setExportingCsv(false);
    }
  };

  const scannedRows = timesheets.filter((e) => isBadgeScanEntry(e));
  const scanByPersonMap = scannedRows.reduce((acc, row) => {
    if (!acc[row.user_id]) {
      acc[row.user_id] = {
        user_id: row.user_id,
        employee_number: row.employee_number,
        first_name: row.first_name,
        last_name: row.last_name,
        center_name: row.center_name,
        sessions: 0,
        total_hours: 0,
      };
    }
    acc[row.user_id].sessions += 1;
    acc[row.user_id].total_hours += Number(row.hours_worked || 0);
    return acc;
  }, {});
  const scanByPerson = Object.values(scanByPersonMap)
    .sort((a, b) => b.total_hours - a.total_hours);
  const scannedDistinctPeople = scanByPerson.length;
  const scannedTotalSessions = scannedRows.length;
  const scannedTotalHours = scanByPerson.reduce((sum, p) => sum + p.total_hours, 0);
  const scannedAvgHoursPerPerson = scannedDistinctPeople ? scannedTotalHours / scannedDistinctPeople : 0;
  const scannedAvgHoursPerSession = scannedTotalSessions ? scannedTotalHours / scannedTotalSessions : 0;
  const scannedInProgress = scannedRows.filter((row) => !row.clock_out).length;
  const scannedCompleted = scannedRows.filter((row) => !!row.clock_out).length;
  const scanChartData = scanByPerson
    .slice(0, 10)
    .map((p) => ({
      name: `${String(p.first_name || '').charAt(0)}. ${p.last_name || ''}`.trim(),
      hours: Number(p.total_hours || 0),
    }));

  const chartData = summary?.hoursByStaff?.slice(0, 10).map(s => ({
    name: `${s.first_name[0]}. ${s.last_name}`,
    hours: parseFloat(s.total_hours) || 0,
  })) || [];

  const totalHours = filteredTimesheets.reduce((sum, e) => sum + Number(e.hours_worked || 0), 0).toFixed(1);
  const attendanceRate = operations?.attendance?.attendance_rate_percent ?? 0;
  const noShowRate = operations?.transport?.no_show_rate_percent ?? 0;
  const unresolvedIncidents = safety?.overview?.unresolved_incidents ?? 0;
  const estimatedCost = operations?.costs?.estimated_total_cost ?? 0;
  const k = kpis?.kpis || {};
  const globalKpiLabelByShort = {
    'Social.': tr('Socialisation', 'Socialization'),
    'Autonom.': tr('Autonomie', 'Autonomy'),
    'Famille': tr('Satisfaction famille', 'Family satisfaction'),
    'Partic.': tr('Participation activités', 'Activity participation'),
    'Hospi.': tr('Réduction hospitalisation', 'Hospitalization reduction'),
    'Soins': tr('Suivi des soins', 'Care follow-up'),
    'Repas': tr('Satisfaction repas', 'Meal satisfaction'),
    'Prés.': tr('Présence', 'Presence'),
  };
  const topPatientKpiLabelByKey = {
    presence: tr('Présence', 'Presence'),
    participation: tr('Participation', 'Participation'),
    socialization: tr('Socialisation', 'Socialization'),
  };
  const lateness = operations?.lateness || {};
  const globalKpiChartData = [
    { label: 'Social.', value: Number(k.socialization_rate_percent || 0) },
    { label: 'Autonom.', value: Number(k.autonomy_progress_rate_percent || 0) },
    { label: 'Famille', value: Number(k.family_caregiver_satisfaction_rate_percent || 0) },
    { label: 'Partic.', value: Number(k.user_activity_participation_rate_percent || 0) },
    { label: 'Hospi.', value: Number(k.hospitalization_reduction_rate_percent || 0) },
    { label: 'Soins', value: Number(k.care_followup_rate_percent || 0) },
    { label: 'Repas', value: Number(k.meal_satisfaction_rate_percent || 0) },
    { label: 'Prés.', value: Number(k.presence_rate_percent || 0) },
  ];
  const patientKpiChartData = (patientKpis || [])
    .slice()
    .sort((a, b) => Number(b.presence_rate_percent || 0) - Number(a.presence_rate_percent || 0))
    .slice(0, 8)
    .map((p) => ({
      name: `${String(p.first_name || '').charAt(0)}. ${p.last_name || ''}`.trim(),
      presence: Number(p.presence_rate_percent || 0),
      participation: Number(p.user_activity_participation_rate_percent || 0),
      socialization: Number(p.socialization_rate_percent || 0),
    }));
  const reportCardHeaderStyle = {
    background: 'linear-gradient(90deg, rgba(13,148,136,0.22) 0%, rgba(13,148,136,0.12) 100%)',
    borderBottom: '1px solid rgba(13,148,136,0.28)',
    borderLeft: '4px solid rgba(13,148,136,0.9)',
    color: '#0f172a',
  };

  return (
    <div>
      <div className="page-header">
        <div><h2>{t('reports.title')}</h2></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={openPdfOptions}>🧾 {tr('Exporter en PDF', 'Export PDF')}</button>
          <button className="btn btn-primary" onClick={openCsvOptions}>📥 {t('reports.export')}</button>
        </div>
      </div>
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div className="flex gap-4 items-center flex-wrap">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{t('reports.startDate')}</label>
            <input type="datetime-local" className="form-input" value={startDateTime} onChange={e => setStartDateTime(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{t('reports.endDate')}</label>
            <input type="datetime-local" className="form-input" value={endDateTime} onChange={e => setEndDateTime(e.target.value)} />
          </div>
          <div style={{ marginTop: 20 }}>
            <button
              className="btn btn-outline"
              onClick={() => {
                setStartDateTime(toDateTimeLocal(startOfMonth(new Date())));
                setEndDateTime(toDateTimeLocal(endOfMonth(new Date())));
              }}
            >
              {tr('Mois actuel', 'Current month')}
            </button>
          </div>
        </div>
      </div>
      {showPdfOptions && (
        <AccessibleModal
          title={tr('Options export PDF', 'PDF export options')}
          onClose={() => setShowPdfOptions(false)}
          maxWidth={760}
          footer={(
            <>
              <button className="btn btn-secondary" type="button" onClick={() => setShowPdfOptions(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" type="button" onClick={exportPointagePdf} disabled={exportingPdf}>
                {exportingPdf ? t('common.loading') : tr('Exporter en PDF', 'Export PDF')}
              </button>
            </>
          )}
        >
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('reports.startDate')}</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={pdfStartDateTime}
                    onChange={(e) => setPdfStartDateTime(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('reports.endDate')}</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={pdfEndDateTime}
                    onChange={(e) => setPdfEndDateTime(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 18, margin: '8px 0 12px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="radio" checked={pdfMode === 'all'} onChange={() => setPdfMode('all')} />
                  {tr('Exporter tous les blocs', 'Export all blocks')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="radio" checked={pdfMode === 'custom'} onChange={() => setPdfMode('custom')} />
                  {tr('Choix personnalisé', 'Custom selection')}
                </label>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 8,
                  opacity: pdfMode === 'custom' ? 1 : 0.6,
                }}
              >
                {pdfBlockList.map((b) => (
                  <label key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!pdfBlocks[b.key]}
                      onChange={(e) => setPdfBlocks((prev) => ({ ...prev, [b.key]: e.target.checked }))}
                      disabled={pdfMode !== 'custom'}
                    />
                    {b.label}
                  </label>
                ))}
              </div>
        </AccessibleModal>
      )}
      {showCsvOptions && (
        <AccessibleModal
          title={tr('Options export CSV', 'CSV export options')}
          onClose={() => setShowCsvOptions(false)}
          maxWidth={760}
          footer={(
            <>
              <button className="btn btn-secondary" type="button" onClick={() => setShowCsvOptions(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" type="button" onClick={exportPointageCsv} disabled={exportingCsv}>
                {exportingCsv ? t('common.loading') : tr('Exporter en CSV', 'Export CSV')}
              </button>
            </>
          )}
        >
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('reports.startDate')}</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={csvStartDateTime}
                    onChange={(e) => setCsvStartDateTime(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('reports.endDate')}</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={csvEndDateTime}
                    onChange={(e) => setCsvEndDateTime(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 18, margin: '8px 0 12px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="radio" checked={csvMode === 'all'} onChange={() => setCsvMode('all')} />
                  {tr('Exporter tous les blocs', 'Export all blocks')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="radio" checked={csvMode === 'custom'} onChange={() => setCsvMode('custom')} />
                  {tr('Choix personnalisé', 'Custom selection')}
                </label>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 8,
                  opacity: csvMode === 'custom' ? 1 : 0.6,
                }}
              >
                {pdfBlockList.map((b) => (
                  <label key={`csv-${b.key}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!csvBlocks[b.key]}
                      onChange={(e) => setCsvBlocks((prev) => ({ ...prev, [b.key]: e.target.checked }))}
                      disabled={csvMode !== 'custom'}
                    />
                    {b.label}
                  </label>
                ))}
              </div>
        </AccessibleModal>
      )}

      {/* Stats */}
      {isAdminOrManager && (
        <div className="stats-grid" style={{ marginBottom: 32 }}>
          <div className="stat-card">
            <div className="stat-icon">⏱️</div>
            <div className="stat-value">{totalHours}h</div>
            <div className="stat-label">{t('reports.totalHours')}</div>
          </div>
          <div className="stat-card info">
            <div className="stat-icon">👥</div>
            <div className="stat-value">{summary?.hoursByStaff?.length || 0}</div>
            <div className="stat-label">{tr('Employés actifs', 'Active employees')}</div>
          </div>
          <div className="stat-card success">
            <div className="stat-icon">📈</div>
            <div className="stat-value">{attendanceRate}%</div>
            <div className="stat-label">{tr('Taux de présence activités', 'Activity attendance rate')}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon">🚐</div>
            <div className="stat-value">{noShowRate}%</div>
            <div className="stat-label">{tr('Absences transport', 'Transport no-show')}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon">⏰</div>
            <div className="stat-value">{lateness.late_shifts || 0}</div>
            <div className="stat-label">{tr('Retardataires', 'Late employees')}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
              {Number(lateness.late_rate_percent || 0).toFixed(1)}% {tr('des quarts', 'of shifts')}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚠️</div>
            <div className="stat-value">{unresolvedIncidents}</div>
            <div className="stat-label">{tr('Incidents non résolus', 'Unresolved incidents')}</div>
          </div>
          {summary && (
            <div className="stat-card info">
              <div className="stat-icon">📋</div>
              <div className="stat-value">{summary.pendingLeaves}</div>
              <div className="stat-label">{t('dashboard.pendingLeaves')}</div>
            </div>
          )}
          {summary && (
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-value">{summary.activeNow}</div>
              <div className="stat-label">{t('dashboard.activeStaff')}</div>
            </div>
          )}
          <div className="stat-card">
            <div className="stat-icon">💲</div>
            <div className="stat-value">{Number(estimatedCost).toFixed(0)}$</div>
            <div className="stat-label">{tr('Coût opérationnel estimé', 'Estimated operational cost')}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
              {tr('Transport', 'Transport')}: {Number(operations?.costs?.transport_trip_cost || 0).toFixed(2)}$ / {tr('trajet', 'trip')}
            </div>
          </div>
        </div>
      )}

      <div className="grid-2" style={{ gap: 24, marginBottom: 32 }}>
        {/* Chart */}
        <div className="card">
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">📊 {t('reports.workHours')} (Top 10)</span>
          </div>
          <div className="card-body" style={{ padding: '16px 8px' }}>
            {loading ? (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner" />
              </div>
            ) : chartData.length === 0 ? (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)' }}>
                {t('common.noData')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => [`${v}h`, t('reports.totalHours')]} />
                  <Bar dataKey="hours" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Time at center top 10 */}
        <div className="card">
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">📊 {tr('Temps au centre (Top 10)', 'Time spent at center (Top 10)')}</span>
          </div>
          <div className="card-body" style={{ padding: '16px 8px' }}>
            {loading ? (
              <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner" />
              </div>
            ) : scanChartData.length === 0 ? (
              <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)' }}>
                {t('common.noData')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={scanChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}h`, tr('Temps total', 'Total time')]} />
                  <Bar dataKey="hours" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Center coverage */}
      {summary?.centerStaff && (
        <div className="card" style={{ marginBottom: 32 }}>
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">🏢 {t('reports.coverage')}</span>
          </div>
          <div className="card-body">
            {summary.centerStaff.map(c => (
              <div key={c.name} style={{ marginBottom: 16 }}>
                <div className="flex justify-between" style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{c.name}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)' }}>{c.staff_count}</span>
                </div>
                <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', background: 'var(--primary)', borderRadius: 3,
                    width: `${Math.min(100, (c.staff_count / 20) * 100)}%`
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="stats-grid" style={{ marginBottom: 32 }}>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-value">{scannedDistinctPeople}</div>
          <div className="stat-label">{tr('Personnes ayant scanné', 'People who scanned')}</div>
        </div>
        <div className="stat-card info">
          <div className="stat-icon">🔁</div>
          <div className="stat-value">{scannedTotalSessions}</div>
          <div className="stat-label">{tr('Sessions IN/OUT', 'IN/OUT sessions')}</div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon">⏱️</div>
          <div className="stat-value">{scannedTotalHours.toFixed(1)}h</div>
          <div className="stat-label">{tr('Temps total passé au centre', 'Total time spent at center')}</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-icon">📊</div>
          <div className="stat-value">{scannedAvgHoursPerPerson.toFixed(2)}h</div>
          <div className="stat-label">{tr('Moyenne par personne', 'Average per person')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🧮</div>
          <div className="stat-value">{scannedAvgHoursPerSession.toFixed(2)}h</div>
          <div className="stat-label">{tr('Moyenne par session', 'Average per session')}</div>
        </div>
        <div className="stat-card info">
          <div className="stat-icon">✅</div>
          <div className="stat-value">{scannedCompleted}</div>
          <div className="stat-label">{tr('Sessions complétées', 'Completed sessions')}</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
            {tr('En cours', 'In progress')}: {scannedInProgress}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 32 }}>
        <div className="card-header" style={reportCardHeaderStyle}>
          <span className="card-title">⏰ {tr('Retards employés', 'Employee lateness')}</span>
          <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>
            {tr('Total', 'Total')}: {lateness.total_late_minutes || 0} min • {tr('Moyenne', 'Average')}: {Number(lateness.avg_late_minutes || 0).toFixed(1)} min
          </span>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{tr('Employé', 'Employee')}</th>
                <th>No.</th>
                <th>{tr('Début quart', 'Shift start')}</th>
                <th>{tr('Pointage', 'Clock-in')}</th>
                <th>{tr('Retard (min)', 'Delay (min)')}</th>
              </tr>
            </thead>
            <tbody>
              {!lateness.late_employees?.length && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 24 }}>{tr('Aucun retard sur la période', 'No lateness during this period')}</td></tr>
              )}
              {(lateness.late_employees || []).map((r) => (
                <tr key={r.shift_id}>
                  <td style={{ fontWeight: 600 }}>{r.first_name} {r.last_name}</td>
                  <td>{r.employee_number || '—'}</td>
                  <td>{r.start_time ? new Date(r.start_time).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—'}</td>
                  <td>{r.first_clock_in ? new Date(r.first_clock_in).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—'}</td>
                  <td><strong>{r.late_minutes || 0}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 24, marginBottom: 32 }}>
        <div className="card">
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">🎯 {t('reports.kpi.title')}</span>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.socializationRate')}</div>
                <strong>{Number(k.socialization_rate_percent || 0).toFixed(2)}%</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.autonomyProgressRate')}</div>
                <strong>{Number(k.autonomy_progress_rate_percent || 0).toFixed(2)}%</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.familySatisfactionRate')}</div>
                <strong>{Number(k.family_caregiver_satisfaction_rate_percent || 0).toFixed(2)}%</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.fallsCount')}</div>
                <strong>{k.falls_count || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.activityParticipationRate')}</div>
                <strong>{Number(k.user_activity_participation_rate_percent || 0).toFixed(2)}%</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.activityFrequencyPerPatient')}</div>
                <strong>{Number(k.user_activity_frequency_per_patient || 0).toFixed(2)}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.hospitalizationReductionRate')}</div>
                <strong>{Number(k.hospitalization_reduction_rate_percent || 0).toFixed(2)}%</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.careFollowupRate')}</div>
                <strong>{Number(k.care_followup_rate_percent || 0).toFixed(2)}%</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.mealSatisfactionRate')}</div>
                <strong>{Number(k.meal_satisfaction_rate_percent || 0).toFixed(2)}%</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{t('reports.kpi.presenceRate')}</div>
                <strong>{Number(k.presence_rate_percent || 0).toFixed(2)}%</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">⚙️ {tr('Opérations', 'Operations')}</span>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Inscriptions activités', 'Activity registrations')}</div>
                <strong>{operations?.attendance?.total_registrations || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Présences', 'Attendances')}</div>
                <strong>{operations?.attendance?.total_attended || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr("Liste d'attente", 'Waitlist')}</div>
                <strong>{operations?.attendance?.total_waitlisted || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Trajets complétés', 'Completed trips')}</div>
                <strong>{operations?.transport?.completed_trips || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Trajets annulés', 'Cancelled trips')}</div>
                <strong>{operations?.transport?.cancelled_trips || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Retard moyen (min)', 'Average delay (min)')}</div>
                <strong>{operations?.transport?.avg_delay_minutes || 0}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">🛡️ {tr('Sécurité', 'Safety')}</span>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Incidents total', 'Total incidents')}</div>
                <strong>{safety?.overview?.total_incidents || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Chutes', 'Falls')}</div>
                <strong>{safety?.overview?.total_falls || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Suivis requis', 'Required follow-ups')}</div>
                <strong>{safety?.overview?.followup_required || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Suivis en retard', 'Overdue follow-ups')}</div>
                <strong>{safety?.overview?.overdue_followups || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Assignations actives', 'Active assignments')}</div>
                <strong>{operations?.assignments?.active_assignments || 0}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Assignations primaires', 'Primary assignments')}</div>
                <strong>{operations?.assignments?.active_primary_assignments || 0}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 24, marginBottom: 32 }}>
        <div className="card">
        <div className="card-header" style={reportCardHeaderStyle}>
          <span className="card-title">📈 {tr('KPI globaux (%)', 'Global KPIs (%)')}</span>
        </div>
          <div className="card-body" style={{ padding: '12px 8px' }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={globalKpiChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, _name, item) => [`${Number(v).toFixed(1)}%`, globalKpiLabelByShort[item?.payload?.label] || item?.payload?.label || 'KPI']} />
                <Bar dataKey="value" fill="var(--primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">🧑‍🤝‍🧑 {tr('Top usagers KPI (%)', 'Top patient KPIs (%)')}</span>
          </div>
          <div className="card-body" style={{ padding: '12px 8px' }}>
            {patientKpiChartData.length === 0 ? (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)' }}>
                {t('common.noData')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={patientKpiChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, name) => [`${Number(v).toFixed(1)}%`, topPatientKpiLabelByKey[name] || name]} />
                  <Bar dataKey="presence" fill="#2a9d8f" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="participation" fill="#4f6d7a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="socialization" fill="#f4a261" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 32 }}>
        <div className="card-header" style={reportCardHeaderStyle}>
          <span className="card-title">💰 {tr('Détail des coûts estimés', 'Estimated cost breakdown')}</span>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Coût horaire personnel', 'Staff hourly cost')}</div>
              <strong>{Number(operations?.costs?.staff_hourly_cost || 0).toFixed(2)}$ / h</strong>
            </div>
            <div>
              <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Coût unitaire transport', 'Transport unit cost')}</div>
              <strong>{Number(operations?.costs?.transport_trip_cost || 0).toFixed(2)}$ / {tr('trajet', 'trip')}</strong>
            </div>
            <div>
              <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Heures totales personnel', 'Total staff hours')}</div>
              <strong>{Number(operations?.costs?.total_staff_hours || 0).toFixed(2)} h</strong>
            </div>
            <div>
              <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Trajets complétés', 'Completed trips')}</div>
              <strong>{operations?.transport?.completed_trips || 0}</strong>
            </div>
            <div>
              <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Coût personnel estimé', 'Estimated staff cost')}</div>
              <strong>{Number(operations?.costs?.estimated_staff_cost || 0).toFixed(2)}$</strong>
            </div>
            <div>
              <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>{tr('Coût transport estimé', 'Estimated transport cost')}</div>
              <strong>{Number(operations?.costs?.estimated_transport_cost || 0).toFixed(2)}$</strong>
            </div>
          </div>
        </div>
      </div>

      {operations?.staffLoad?.length ? (
        <div className="card" style={{ marginBottom: 32 }}>
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">👩‍⚕️ {tr('Charge du personnel', 'Staff workload')}</span>
            <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>{filteredStaffLoadRows.length}/{staffLoadRows.length}</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>{tr('Employé', 'Employee')}</th>
                  <th>No.</th>
                  <th>{tr('Fonction', 'Function')}</th>
                  <th>{tr('Heures', 'Hours')}</th>
                </tr>
                <tr>
                  <th>
                    <select className="form-select" value={staffEmployeeFilter} onChange={(e) => setStaffEmployeeFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {staffEmployeeOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={staffNoFilter} onChange={(e) => setStaffNoFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {staffNoOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={staffFunctionFilter} onChange={(e) => setStaffFunctionFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {staffFunctionOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={staffHoursFilter} onChange={(e) => setStaffHoursFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {staffHoursOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredStaffLoadRows.map(s => (
                  <tr key={s.staff_id}>
                    <td>{s.first_name} {s.last_name}</td>
                    <td>{s.employee_number}</td>
                    <td>{s.job_function || '—'}</td>
                    <td><strong>{s.hours_worked}h</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {patientKpis?.length ? (
        <div className="card" style={{ marginBottom: 32 }}>
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">🧑‍🤝‍🧑 {t('reports.kpi.patientTableTitle')}</span>
            <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>{filteredPatientKpis.length}/{patientKpis.length}</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>{t('reports.employee')}</th>
                  <th>{t('reports.kpi.socializationRate')}</th>
                  <th>{t('reports.kpi.autonomyProgressRate')}</th>
                  <th>{t('reports.kpi.familySatisfactionRate')}</th>
                  <th>{t('reports.kpi.fallsCount')}</th>
                  <th>{t('reports.kpi.activityParticipationRate')}</th>
                  <th>{t('reports.kpi.hospitalizationReductionRate')}</th>
                  <th>{t('reports.kpi.careFollowupRate')}</th>
                  <th>{t('reports.kpi.mealSatisfactionRate')}</th>
                  <th>{t('reports.kpi.presenceRate')}</th>
                </tr>
                <tr>
                  <th><select className="form-select" value={kpiUserFilter} onChange={(e) => setKpiUserFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiUserOptions.map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={kpiSocialFilter} onChange={(e) => setKpiSocialFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiValueOptions('socialization_rate_percent').map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={kpiAutonomyFilter} onChange={(e) => setKpiAutonomyFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiValueOptions('autonomy_progress_rate_percent').map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={kpiFamilyFilter} onChange={(e) => setKpiFamilyFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiValueOptions('family_caregiver_satisfaction_rate_percent').map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={kpiFallsFilter} onChange={(e) => setKpiFallsFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiFallsOptions.map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={kpiParticipationFilter} onChange={(e) => setKpiParticipationFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiValueOptions('user_activity_participation_rate_percent').map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={kpiHospitalFilter} onChange={(e) => setKpiHospitalFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiValueOptions('hospitalization_reduction_rate_percent').map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={kpiFollowupFilter} onChange={(e) => setKpiFollowupFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiValueOptions('care_followup_rate_percent').map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={kpiMealFilter} onChange={(e) => setKpiMealFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiValueOptions('meal_satisfaction_rate_percent').map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={kpiPresenceFilter} onChange={(e) => setKpiPresenceFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{kpiValueOptions('presence_rate_percent').map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                </tr>
              </thead>
              <tbody>
                {filteredPatientKpis.map((p) => (
                  <tr key={p.patient_id}>
                    <td style={{ fontWeight: 600 }}>{p.first_name} {p.last_name}</td>
                    <td>{Number(p.socialization_rate_percent || 0).toFixed(1)}%</td>
                    <td>{Number(p.autonomy_progress_rate_percent || 0).toFixed(1)}%</td>
                    <td>{Number(p.family_caregiver_satisfaction_rate_percent || 0).toFixed(1)}%</td>
                    <td>{p.falls_count || 0}</td>
                    <td>{Number(p.user_activity_participation_rate_percent || 0).toFixed(1)}%</td>
                    <td>{Number(p.hospitalization_reduction_rate_percent || 0).toFixed(1)}%</td>
                    <td>{Number(p.care_followup_rate_percent || 0).toFixed(1)}%</td>
                    <td>{Number(p.meal_satisfaction_rate_percent || 0).toFixed(1)}%</td>
                    <td>{Number(p.presence_rate_percent || 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {safety?.unresolvedIncidents?.length ? (
        <div className="card" style={{ marginBottom: 32 }}>
          <div className="card-header" style={reportCardHeaderStyle}>
            <span className="card-title">🚨 {tr('Incidents à traiter', 'Incidents to process')}</span>
            <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>{filteredIncidents.length}/{unresolvedRows.length}</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>{tr('Usager', 'Patient')}</th>
                  <th>{tr('Type', 'Type')}</th>
                  <th>{tr('Sévérité', 'Severity')}</th>
                  <th>{tr('Statut', 'Status')}</th>
                  <th>{tr('Suivi', 'Follow-up')}</th>
                </tr>
                <tr>
                  <th><select className="form-select" value={incDateFilter} onChange={(e) => setIncDateFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{incidentDateOptions.map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={incPatientFilter} onChange={(e) => setIncPatientFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{incidentPatientOptions.map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={incTypeFilter} onChange={(e) => setIncTypeFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{incidentTypeOptions.map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={incSeverityFilter} onChange={(e) => setIncSeverityFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{incidentSeverityOptions.map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={incStatusFilter} onChange={(e) => setIncStatusFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{incidentStatusOptions.map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                  <th><select className="form-select" value={incFollowupFilter} onChange={(e) => setIncFollowupFilter(e.target.value)}><option value="all">{tr('Tous', 'All')}</option>{incidentFollowupOptions.map((v) => <option key={v} value={v}>{v}</option>)}</select></th>
                </tr>
              </thead>
              <tbody>
                {filteredIncidents.map(i => (
                  <tr key={i.id}>
                    <td>{i.occurred_at ? new Date(i.occurred_at).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—'}</td>
                    <td>{i.patient_first_name} {i.patient_last_name}</td>
                    <td>{i.incident_type}</td>
                    <td>{i.severity}</td>
                    <td>{i.status}</td>
                    <td>{i.followup_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Timesheet table */}
      <div className="card">
        <div className="card-header" style={reportCardHeaderStyle}>
          <span className="card-title">📋 {t('reports.timesheets')}</span>
          <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>
            {filteredTimesheets.length}/{timesheets.length} {tr('entrées', 'entries')}
          </span>
        </div>
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t('reports.employee')}</th>
                  <th>No.</th>
                  <th>Date</th>
                  <th>{t('timeclock.entry')}</th>
                  <th>{t('timeclock.exit')}</th>
                  <th>{t('reports.totalHours')}</th>
                  <th>{tr('Total (min)', 'Total (min)')}</th>
                  <th>{tr('Centre', 'Center')}</th>
                </tr>
                <tr>
                  <th>
                    <select className="form-select" value={tsEmployeeFilter} onChange={(e) => setTsEmployeeFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {timesheetEmployeeOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={tsNoFilter} onChange={(e) => setTsNoFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {timesheetNoOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={tsDateFilter} onChange={(e) => setTsDateFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {timesheetDateOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={tsEntryFilter} onChange={(e) => setTsEntryFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {timesheetEntryOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={tsExitFilter} onChange={(e) => setTsExitFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {timesheetExitOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={tsHoursFilter} onChange={(e) => setTsHoursFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {timesheetHoursOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={tsMinutesFilter} onChange={(e) => setTsMinutesFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {timesheetMinutesOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
                  <th>
                    <select className="form-select" value={tsCenterFilter} onChange={(e) => setTsCenterFilter(e.target.value)}>
                      <option value="all">{tr('Tous', 'All')}</option>
                      {timesheetCenterOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTimesheets.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 32 }}>{t('common.noData')}</td></tr>
                )}
                {filteredTimesheets.map((e, i) => (
                  <tr key={`${e.user_id}-${i}`}>
                    <td style={{ fontWeight: 600 }}>{e.first_name} {e.last_name}</td>
                    <td><code style={{ fontSize: 11, background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4 }}>{e.employee_number}</code></td>
                    <td style={{ fontSize: 13 }}>{e.work_date ? format(new Date(e.work_date), 'yyyy-MM-dd') : '—'}</td>
                    <td>{e.clock_in ? format(new Date(e.clock_in), 'HH:mm') : '—'}</td>
                    <td>{e.clock_out ? format(new Date(e.clock_out), 'HH:mm') : <span className="badge badge-success">{tr('En cours', 'In progress')}</span>}</td>
                    <td><strong>{parseFloat(e.hours_worked || 0).toFixed(2)}h</strong></td>
                    <td><strong>{Math.round(Number(e.hours_worked || 0) * 60)} min</strong></td>
                    <td style={{ fontSize: 13 }}>{e.center_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
